import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MESSAGE_RESEAU } from '../api';
import { useAuth } from '../auth/contexte-auth';
import { useFile } from '../horsligne/contexte-file';
import { maintenantServeur } from '../horsligne/horloge';
import { heureDeTunis, saisies } from '../horsligne/textes';

/**
 * État de la file d'envoi (docs/UI.md § Écran 3, mode hors ligne) : bandeau
 * global sous l'en-tête, et ligne d'état du panneau de contact. Rien quand
 * tout va bien ; jamais de contenu de saisie ni de nom.
 */

const VINGT_QUATRE_HEURES_MS = 24 * 60 * 60 * 1000;
/** Au-delà, « nouvel essai à » cède la place à « en échec depuis ». */
const ECHECS_AVANT_ALERTE = 3;

function recharger(): void {
  window.location.reload();
}

interface Ligne {
  cle: string;
  genre: 'hors-ligne' | 'attente' | 'refus' | 'ok';
  texte: string;
  action?: ReactNode;
}

export function BandeauEnvoi() {
  const { instantane: i, envoyerMaintenant, oublierPurgeAnciennete } = useFile();
  const { sessionFragile } = useAuth();
  const maintenant = maintenantServeur();
  const lignes: Ligne[] = [];

  if (i.miseAJourBloquee) {
    lignes.push({
      cle: 'bloquee',
      genre: 'attente',
      texte: 'Mise à jour de l’application : fermez ses autres onglets pour continuer.',
    });
  } else if (i.miseAJourRequise) {
    lignes.push({
      cle: 'mise-a-jour',
      genre: 'attente',
      texte: 'Nouvelle version de l’application : rechargez (votre saisie est gardée).',
      action: (
        <button type="button" className="lien" onClick={recharger}>
          Recharger
        </button>
      ),
    });
  }
  if (i.refusees > 0) {
    lignes.push({
      cle: 'refus',
      genre: 'refus',
      texte: `${saisies(i.refusees)} ${i.refusees > 1 ? 'refusées' : 'refusée'} à revoir`,
      action: <Link to="/envois">Voir</Link>,
    });
  }
  if (i.effaceesPourAnciennete > 0) {
    const n = i.effaceesPourAnciennete;
    lignes.push({
      cle: 'anciennete',
      genre: 'refus',
      texte: `${saisies(n)} de plus de 7 jours ${n > 1 ? 'ont été effacées' : 'a été effacée'} de ce téléphone sans avoir été envoyée${n > 1 ? 's' : ''}.`,
      action: (
        <button type="button" className="lien" onClick={oublierPurgeAnciennete}>
          J’ai compris
        </button>
      ),
    });
  }
  if (i.recensement && i.recensement.illisibles > 0) {
    const n = i.recensement.illisibles;
    lignes.push({
      cle: 'illisibles',
      genre: 'attente',
      texte: `${saisies(n)} ${n > 1 ? 'faites' : 'faite'} avec une version plus récente de l’application : rechargez la page pour ${n > 1 ? 'les envoyer' : 'l’envoyer'}.`,
      action: (
        <button type="button" className="lien" onClick={recharger}>
          Recharger
        </button>
      ),
    });
  }
  if (i.pret && i.stockage === 'memoire') {
    lignes.push({
      cle: 'memoire',
      genre: 'refus',
      texte: 'Ce téléphone refuse l’enregistrement local : restez sur cet écran jusqu’au retour du réseau.',
    });
  }
  const bientotEffacees = i.resumes.filter(
    (r) => r.etat !== 'brouillon' && !r.refus?.contenuEfface && r.expireLe - maintenant < VINGT_QUATRE_HEURES_MS,
  ).length;
  if (bientotEffacees > 0) {
    lignes.push({
      cle: 'expiration',
      genre: 'refus',
      texte: `${saisies(bientotEffacees)} en attente ${bientotEffacees > 1 ? 'seront effacées' : 'sera effacée'} de ce téléphone dans moins de 24 h.`,
      action: <Link to="/envois">Voir</Link>,
    });
  }

  const n = i.enAttente;
  if (i.envoiEnCours) {
    lignes.push({
      cle: 'activite',
      genre: 'attente',
      texte: `Envoi en cours · ${i.envoiEnCours.rang} sur ${i.envoiEnCours.total}`,
    });
  } else if (!i.enLigne) {
    lignes.push({
      cle: 'activite',
      genre: 'hors-ligne',
      texte:
        n === 0
          ? 'Hors ligne. Ce que vous saisissez reste sur ce téléphone.'
          : `Hors ligne · ${saisies(n)} en attente d’envoi`,
    });
  } else if (n > 0 && sessionFragile) {
    lignes.push({ cle: 'activite', genre: 'attente', texte: `${saisies(n)} en attente · reprise de la session…` });
  } else if (n > 0 && i.echecsConsecutifs > 0) {
    const raison =
      i.dernierePanne === null || i.dernierePanne === MESSAGE_RESEAU ? 'réseau indisponible' : i.dernierePanne;
    const suite =
      i.echecsConsecutifs >= ECHECS_AVANT_ALERTE && i.enEchecDepuis !== null
        ? `envoi en échec depuis ${heureDeTunis(i.enEchecDepuis)} (${raison})`
        : i.prochainEssai !== null
          ? `nouvel essai à ${heureDeTunis(Math.max(i.prochainEssai, maintenant))}`
          : 'nouvel essai bientôt';
    lignes.push({
      cle: 'activite',
      genre: 'attente',
      texte: `${saisies(n)} en attente · ${suite}`,
      action: (
        <button type="button" className="lien" onClick={envoyerMaintenant}>
          Réessayer
        </button>
      ),
    });
  } else if (i.toutEnvoyeLe !== null && n === 0) {
    lignes.push({ cle: 'activite', genre: 'ok', texte: `Tout est envoyé · ${heureDeTunis(i.toutEnvoyeLe)}` });
  }

  if (lignes.length === 0) return <div role="status" className="etat-envois etat-envois--vide" />;
  // Au plus deux lignes : l'alerte la plus importante et l'activité de la file.
  const affichees = lignes.length > 2 ? [lignes[0]!, lignes[lignes.length - 1]!] : lignes;
  return (
    <div role="status" className="etat-envois">
      {affichees.map((ligne) => (
        <p key={ligne.cle} className={`etat-envois__ligne etat-envois--${ligne.genre}`}>
          <span>{ligne.texte}</span>
          {ligne.action}
        </p>
      ))}
    </div>
  );
}

/** Où en est la saisie du contact ouvert, sous le bandeau du panneau. */
export function LigneEtatSaisie({
  contactId,
  brouillonNonEnregistre,
  enregistreLe,
}: {
  contactId: string;
  brouillonNonEnregistre: boolean;
  enregistreLe: string | number | null;
}) {
  const { instantane: i } = useFile();
  const resume = i.resumes.find((r) => r.contactId === contactId && r.etat !== 'brouillon');
  let etat: string | null = null;
  if (i.envoiEnCours?.contactId === contactId) etat = 'Envoi en cours…';
  else if (resume?.etat === 'refuse') etat = 'Refusé par le serveur · à revoir ci-dessous';
  else if (brouillonNonEnregistre) etat = 'Modifications gardées sur ce téléphone · pas encore enregistrées';
  else if (resume?.etat === 'en_attente') {
    etat = `Enregistré sur ce téléphone à ${heureDeTunis(resume.modifieLe)} · envoi au retour du réseau`;
  } else if (enregistreLe !== null) etat = `Enregistré sur le serveur à ${heureDeTunis(enregistreLe)}`;

  const texte = i.enLigne
    ? etat
    : etat === null
      ? 'Hors ligne · votre saisie est gardée sur ce téléphone'
      : `Hors ligne · ${etat}`;
  if (texte === null) return null;
  return (
    <p className={`ligne-etat-saisie${i.enLigne ? '' : ' ligne-etat-saisie--hors-ligne'}`} role="status">
      {texte}
    </p>
  );
}
